import React, { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles, Target, Wallet } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/formatters';
import { useRiqueChatStore } from '@/stores/riqueChatStore';
import { DiretoriaGauge } from '@/components/Diretoria/DiretoriaGauge';
import { DiretoriaSimularModal } from '@/components/Diretoria/DiretoriaSimularModal';
import { DiretoriaSqlButton } from '@/components/Diretoria/DiretoriaSqlButton';
import {
  DiretoriaLocacoesDetalheModal,
  type LocacoesDetalheKind,
} from '@/components/Diretoria/DiretoriaLocacoesDetalheModal';

export interface DiretoriaProjecoesMetas {
  companyId: string;
  ano: number;
  mes: number;
  locoesCriadas: number | null;
  cancelamentos: number | null;
}

export interface DiretoriaProjecoesSectionProps {
  companyId: string;
  periodFrom: string;
  periodTo: string;
  periodLabel: string;
  criadas: number;
  canceladas: number;
  canceladasLt90: number;
  /** Snapshot de locações ativas (denominador do turnover). */
  locacoesAtivas?: number;
  /** Canceladas no mês civil de dataTo (numerador do turnover). */
  canceladasMesRef?: number;
  turnoverPct: number | null;
  metas: DiretoriaProjecoesMetas | undefined;
  mensal: Array<{ name: string; novas: number; canceladas: number }>;
  tiposLocacao: Array<{ tipo: string; quantidade: number; canceladas: number }>;
  /** SQL do fluxo criadas/canceladas (fallback se contexto ainda não carregou). */
  querySqlFluxo?: string | null;
}

type ProjecaoChipId = 'gap' | 'definir-meta' | 'proximo' | 'cancel' | 'mix';

type ProjecaoChip =
  | { id: ProjecaoChipId; label: string; kind: 'prompt'; prompt: string }
  | { id: ProjecaoChipId; label: string; kind: 'focus-metas' };

function buildProjecaoChips(ctx: {
  criadas: number;
  canceladas: number;
  metaCriadas: number | null;
  metaCancel: number | null;
  periodLabel: string;
}): ProjecaoChip[] {
  const gap = ctx.metaCriadas != null ? ctx.criadas - ctx.metaCriadas : null;
  const chips: ProjecaoChip[] = [];

  if (ctx.metaCriadas == null) {
    chips.push({
      id: 'definir-meta',
      label: 'Definir meta e projetar',
      kind: 'focus-metas',
    });
  } else {
    const gapLabel =
      gap == null ? 'Gap vs meta' : gap >= 0 ? `Gap +${gap}` : `Gap ${gap}`;
    const gapTxt =
      gap! >= 0
        ? `Estamos ${gap} acima da meta de ${ctx.metaCriadas} locações (${ctx.criadas} criadas no período).`
        : `Faltam ${Math.abs(gap!)} locações para a meta de ${ctx.metaCriadas} (${ctx.criadas} criadas no período).`;
    chips.push({
      id: 'gap',
      label: gapLabel,
      kind: 'prompt',
      prompt: `${gapTxt} Meta salva: ${ctx.metaCriadas}. Realizado: ${ctx.criadas} criadas e ${ctx.canceladas} canceladas em ${ctx.periodLabel}. Projete o fechamento, compare com a meta e sugira ações objetivas.`,
    });
  }

  chips.push(
    {
      id: 'proximo',
      label: 'Próximo mês',
      kind: 'prompt',
      prompt: `Com base no histórico e no realizado de ${ctx.periodLabel} (${ctx.criadas} criadas, ${ctx.canceladas} canceladas${ctx.metaCriadas != null ? `, meta ${ctx.metaCriadas}` : ''}), projete locações e cancelamentos para o próximo mês e indique cenários (conservador, base, otimista).`,
    },
    {
      id: 'cancel',
      label:
        ctx.metaCancel != null
          ? `Cancel. ${ctx.canceladas}/${ctx.metaCancel}`
          : 'Cancelamentos',
      kind: 'prompt',
      prompt: `Analise o turnover e os cancelamentos (${ctx.canceladas} no período${ctx.metaCancel != null ? `, meta ${ctx.metaCancel}` : ''}; ${ctx.criadas} criadas). Projete impacto se reduzirmos cancelamentos e sugira 3 ações práticas.`,
    },
    {
      id: 'mix',
      label: 'Mix de tipos',
      kind: 'prompt',
      prompt: `Analise o mix de tipos de contrato no período ${ctx.periodLabel} (${ctx.criadas} criadas, ${ctx.canceladas} canceladas), destaque os que mais geram locações e cancelamentos, e projete onde concentrar esforço no próximo mês.`,
    }
  );

  return chips;
}

function FinKpiCard({
  label,
  value,
  hint,
  sql,
  title,
  highlight,
}: {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  sql?: string | null;
  title: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`relative rounded-lg border p-3 ${
        highlight ? 'border-primary/30 bg-primary/5' : 'border-border/80'
      }`}
    >
      <div className="mb-1 flex items-start justify-between gap-1">
        <p className="text-[11px] uppercase text-muted-foreground">{label}</p>
        <DiretoriaSqlButton sql={sql} source="cobranca" title={title} />
      </div>
      <p className="font-heading text-lg font-bold tabular-nums">{value}</p>
      {hint ? <div className="text-[10px] text-muted-foreground">{hint}</div> : null}
    </div>
  );
}

export const DiretoriaProjecoesSection: React.FC<DiretoriaProjecoesSectionProps> = ({
  companyId,
  periodFrom,
  periodTo,
  periodLabel,
  criadas,
  canceladas,
  canceladasLt90,
  locacoesAtivas = 0,
  canceladasMesRef,
  turnoverPct,
  metas,
  mensal,
  tiposLocacao,
  querySqlFluxo: querySqlFluxoProp,
}) => {
  const queryClient = useQueryClient();
  const openRique = useRiqueChatStore((s) => s.openRique);
  const metasBlockRef = useRef<HTMLDivElement>(null);
  const metaLocacoesInputRef = useRef<HTMLInputElement>(null);
  const [simularOpen, setSimularOpen] = useState(false);
  const [detalheKind, setDetalheKind] = useState<LocacoesDetalheKind | null>(null);
  const [promptIa, setPromptIa] = useState('');
  const [metaAlvo, setMetaAlvo] = useState('');
  const [metaCriadas, setMetaCriadas] = useState<string>('');
  const [metaCancel, setMetaCancel] = useState<string>('');
  const [openingIa, setOpeningIa] = useState(false);
  const [metasHighlight, setMetasHighlight] = useState(false);

  const anoMes = useMemo(() => {
    const d = periodTo.slice(0, 7).split('-');
    return { ano: Number(d[0]) || new Date().getFullYear(), mes: Number(d[1]) || new Date().getMonth() + 1 };
  }, [periodTo]);

  const { data: projecaoCtx } = useQuery({
    queryKey: ['diretoria-projecao-contexto', companyId, periodFrom, periodTo, anoMes.ano, anoMes.mes],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (companyId) params.set('companyId', companyId);
      params.set('dataFrom', periodFrom);
      params.set('dataTo', periodTo);
      params.set('ano', String(anoMes.ano));
      params.set('mes', String(anoMes.mes));
      const res = await api.get(`/reports/diretoria/projecao-contexto?${params.toString()}`);
      return res.data.data as {
        projecao: {
          projetadoBase: number;
          projetadoConservador: number;
          projetadoAgressivo: number;
          horizonteFimMes: string;
          runRateDiarioCriadas: number;
        };
        financeiro: {
          ticketMedio: number;
          ticketMedioSemanal: number;
          semanasPeriodo: number;
          locacoesComTitulo: number;
          receitaVinculada: number;
          receitaPeriodoEstimada: number;
          projetadoConservador: number;
          projetadoBase: number;
          projetadoAgressivo: number;
          metaFinanceira: number | null;
          gapVsMetaFinanceiro: number | null;
          metodo?: string;
          querySqlTicket?: string;
          querySqlFluxo?: string;
        };
      };
    },
    staleTime: 60_000,
  });

  const fin = projecaoCtx?.financeiro;
  const hasFinanceiro = (fin?.ticketMedio ?? 0) > 0;
  const sqlTicket = fin?.querySqlTicket?.trim() || null;
  const sqlFluxo = fin?.querySqlFluxo?.trim() || querySqlFluxoProp?.trim() || null;
  const sqlTicketSemanal = sqlTicket
    ? `${sqlTicket}\n\n-- Ticket médio semanal = receita_periodo_estimada / (dias_periodo / 7)\n-- receita_periodo_estimada = criadas × ticket_medio`
    : null;
  const sqlBaseParts = [sqlFluxo, sqlTicket].filter(Boolean) as string[];
  const sqlEstimPeriodo = sqlBaseParts.length
    ? `${sqlBaseParts.join('\n\n')}\n\n-- Estim. período = criadas (novas_mes) × ticket_medio`
    : null;
  const sqlProjBase = sqlBaseParts.length
    ? `${sqlBaseParts.join('\n\n')}\n\n-- Projeção R$ = locações_projetadas × ticket_medio\n-- Base: criadas + run_rate_diario × dias_até_fim_mês\n-- Conservador: base × 0.9 · Agressivo: base × 1.15`
    : null;
  const sqlMetaFin = sqlTicket
    ? `${sqlTicket}\n\n-- Meta financeira = meta_locacoes_criadas × ticket_medio\n-- Gap = proj. base R$ − meta financeira`
    : null;

  const metaLocacoes = metas?.locoesCriadas ?? null;
  const metaCancelamentos = metas?.cancelamentos ?? null;
  const gapLocacoes = metaLocacoes != null ? criadas - metaLocacoes : null;
  const metaAlvoNum = (() => {
    const n = Number(metaAlvo.trim());
    return metaAlvo.trim() !== '' && Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
  })();

  const contextoResumo = useMemo(() => {
    const parts = [
      periodLabel,
      `${criadas.toLocaleString('pt-BR')} criadas`,
      `${canceladas.toLocaleString('pt-BR')} canceladas`,
    ];
    if (metaLocacoes != null) {
      parts.push(`meta ${metaLocacoes.toLocaleString('pt-BR')}`);
      if (gapLocacoes != null) {
        parts.push(
          gapLocacoes >= 0
            ? `+${gapLocacoes.toLocaleString('pt-BR')} vs meta`
            : `${gapLocacoes.toLocaleString('pt-BR')} vs meta`
        );
      }
    } else {
      parts.push('sem meta de locações');
    }
    if (metaAlvoNum != null) {
      parts.push(`alvo IA ${metaAlvoNum.toLocaleString('pt-BR')}`);
    }
    return parts.join(' · ');
  }, [periodLabel, criadas, canceladas, metaLocacoes, gapLocacoes, metaAlvoNum]);

  const chips = useMemo(
    () =>
      buildProjecaoChips({
        criadas,
        canceladas,
        metaCriadas: metaLocacoes,
        metaCancel: metaCancelamentos,
        periodLabel,
      }),
    [criadas, canceladas, metaLocacoes, metaCancelamentos, periodLabel]
  );

  React.useEffect(() => {
    setMetaCriadas(metas?.locoesCriadas != null ? String(metas.locoesCriadas) : '');
    setMetaCancel(metas?.cancelamentos != null ? String(metas.cancelamentos) : '');
  }, [metas?.locoesCriadas, metas?.cancelamentos, metas?.ano, metas?.mes]);

  React.useEffect(() => {
    if (metaLocacoes == null) return;
    setMetaAlvo((prev) => (prev.trim() === '' ? String(metaLocacoes) : prev));
  }, [metaLocacoes]);

  const saveMetas = useMutation({
    mutationFn: async () => {
      await api.put('/reports/diretoria/metas', {
        companyId: companyId || undefined,
        ano: anoMes.ano,
        mes: anoMes.mes,
        locoesCriadas: metaCriadas.trim() === '' ? null : Number(metaCriadas),
        cancelamentos: metaCancel.trim() === '' ? null : Number(metaCancel),
      });
    },
    onSuccess: () => {
      toast.success('Metas salvas');
      void queryClient.invalidateQueries({ queryKey: ['diretoria-metas'] });
    },
    onError: () => toast.error('Falha ao salvar metas'),
  });

  const focusMetasBlock = () => {
    setMetasHighlight(true);
    metasBlockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => {
      metaLocacoesInputRef.current?.focus();
      metaLocacoesInputRef.current?.select();
    }, 280);
    toast('Defina a meta de locações e salve para projetar o gap.');
    window.setTimeout(() => setMetasHighlight(false), 2800);
  };

  const handleAnalisarIa = (pedidoOverride?: string) => {
    if (openingIa) return;
    let userAsk =
      (pedidoOverride ?? promptIa).trim() ||
      'Projetar locações do período atual, comparar com a meta e sugerir ações com base no histórico e tipos de contrato.';

    if (metaAlvoNum != null) {
      const falta = Math.max(0, metaAlvoNum - criadas);
      userAsk += ` Meta alvo desejada: ${metaAlvoNum} locações (realizado ${criadas}; ${falta > 0 ? `faltam ${falta}` : 'já atingiu ou superou o alvo'}). Explique o que é necessário para chegar nesse número.`;
    }

    if (pedidoOverride) setPromptIa(pedidoOverride);

    const prompt = [
      `Estou na Tela 10 (Diretoria) — Projeções.`,
      `Período: ${periodFrom} a ${periodTo} (${periodLabel}).`,
      `Realizado: ${criadas} locações criadas, ${canceladas} canceladas` +
        (turnoverPct != null ? `, turnover ${turnoverPct.toFixed(1)}%.` : '.'),
      metas?.locoesCriadas != null || metas?.cancelamentos != null
        ? `Metas ${anoMes.mes}/${anoMes.ano}: locações=${metas?.locoesCriadas ?? '—'}, cancelamentos=${metas?.cancelamentos ?? '—'}.`
        : `Sem meta salva para ${anoMes.mes}/${anoMes.ano}.`,
      fin && fin.ticketMedio > 0
        ? `Financeiro: ticket médio R$ ${fin.ticketMedio.toFixed(2)}; estim. período R$ ${fin.receitaPeriodoEstimada.toFixed(2)}; proj. base R$ ${fin.projetadoBase.toFixed(2)}${fin.metaFinanceira != null ? `; meta financeira R$ ${fin.metaFinanceira.toFixed(2)}` : ''}.`
        : null,
      metaAlvoNum != null ? `Meta alvo informada pelo usuário (cenário): ${metaAlvoNum}.` : null,
      `Pedido do usuário: ${userAsk}`,
      `Use a ferramenta get_diretoria_projecao_contexto com dataFrom=${periodFrom}, dataTo=${periodTo}, ano=${anoMes.ano}, mes=${anoMes.mes}` +
        (companyId ? ` e companyId=${companyId}` : '') +
        `. Baseie números e sugestões apenas no retorno da ferramenta.`,
    ]
      .filter(Boolean)
      .join('\n');

    setOpeningIa(true);
    openRique({ companyId: companyId || undefined, prompt });
    toast.success('Abrindo Rique com o contexto de projeções…');
    window.setTimeout(() => setOpeningIa(false), 1200);
  };

  const handleChipClick = (chip: ProjecaoChip) => {
    if (chip.kind === 'focus-metas') {
      focusMetasBlock();
      return;
    }
    handleAnalisarIa(chip.prompt);
  };

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="font-heading text-lg font-semibold text-foreground">Projeções</h2>
          <p className="text-sm text-muted-foreground">
            Comparativo com meta · turnover · financeiro · simulação · {periodLabel}
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => setSimularOpen(true)}>
          Simular
        </Button>
      </div>

      <Card className="border-border/80">
        <CardContent className="grid grid-cols-1 gap-6 p-4 md:grid-cols-2 lg:grid-cols-5 lg:items-center">
          <div className="relative flex flex-col items-center">
            <div className="absolute right-0 top-0 z-10">
              <DiretoriaSqlButton
                sql={sqlFluxo}
                source="cobranca"
                title="Locações × meta — SQL"
                stopPropagation
              />
            </div>
            <DiretoriaGauge
              label="Locações × meta"
              value={criadas}
              meta={metas?.locoesCriadas ?? null}
              onClick={() => setDetalheKind('criadas')}
            />
            <p className="mt-0.5 text-[10px] text-muted-foreground">Clique para listar</p>
          </div>
          <div className="relative flex flex-col items-center">
            <div className="absolute right-0 top-0 z-10">
              <DiretoriaSqlButton
                sql={sqlFluxo}
                source="cobranca"
                title="Cancelamentos × meta — SQL"
                stopPropagation
              />
            </div>
            <DiretoriaGauge
              label="Cancelamentos × meta"
              value={canceladas}
              meta={metas?.cancelamentos ?? null}
              onClick={() => setDetalheKind('canceladas')}
            />
            <p className="mt-0.5 text-[10px] text-muted-foreground">Clique para listar</p>
          </div>
          <button
            type="button"
            className="relative flex flex-col items-center justify-center rounded-lg px-2 py-1 text-center transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => setDetalheKind('canceladas_lt90')}
          >
            <div className="absolute right-0 top-0 z-10" onClick={(e) => e.stopPropagation()}>
              <DiretoriaSqlButton
                sql={
                  sqlFluxo
                    ? `${sqlFluxo}\n\n-- canceladas_lt90_mes: mesma regra de canceladas_mes\n-- + DATEDIFF(data_cancelamento, data_inicio_contrato) < 90`
                    : null
                }
                source="cobranca"
                title="Cancel. < 90 dias — SQL"
                stopPropagation
              />
            </div>
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Cancel. &lt; 90 dias
            </p>
            <p className="font-heading text-2xl font-bold tabular-nums">
              {canceladasLt90.toLocaleString('pt-BR')}
            </p>
            <p className="mt-0.5 text-[10px] text-muted-foreground">Clique para listar</p>
          </button>
          <button
            type="button"
            className="relative flex flex-col items-center justify-center rounded-lg px-2 py-1 text-center transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => setDetalheKind('canceladas_mes_ref')}
          >
            <div className="absolute right-0 top-0 z-10" onClick={(e) => e.stopPropagation()}>
              <DiretoriaSqlButton
                sql={
                  sqlFluxo
                    ? `${sqlFluxo}\n\n-- turnover_pct = canceladas_mes_ref / locacoes_ativas × 100\n-- locacoes_ativas: snapshot (data_cancelamento IS NULL, status sem CANCEL)\n-- canceladas_mes_ref: data_cancelamento no mês civil de dataTo (excl. ERRO EM/DE CONTRATO, incl. status ERRO_EM_CONTRATO)`
                    : null
                }
                source="cobranca"
                title="Turnover — SQL"
                stopPropagation
              />
            </div>
            <Target className="mb-1 h-5 w-5 text-muted-foreground" />
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Turnover
            </p>
            <p className="font-heading text-2xl font-bold tabular-nums">
              {turnoverPct != null ? `${turnoverPct.toFixed(1)}%` : '—'}
            </p>
            <p className="text-xs text-muted-foreground">
              {(locacoesAtivas ?? 0).toLocaleString('pt-BR')} ativas ·{' '}
              {(canceladasMesRef ?? canceladas).toLocaleString('pt-BR')} canceladas
            </p>
            <p className="mt-0.5 text-[10px] text-muted-foreground">Clique: canceladas do mês</p>
          </button>
          <div
            ref={metasBlockRef}
            className={`space-y-2 rounded-lg border border-dashed p-3 transition-colors ${
              metasHighlight
                ? 'border-primary bg-primary/5 ring-2 ring-primary/30'
                : 'border-border/80 bg-muted/20'
            }`}
          >
            <p className="text-xs font-medium text-foreground">Metas ({anoMes.mes}/{anoMes.ano})</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-muted-foreground">Locações</label>
                <Input
                  ref={metaLocacoesInputRef}
                  type="number"
                  min={0}
                  value={metaCriadas}
                  onChange={(e) => setMetaCriadas(e.target.value)}
                  placeholder="—"
                  className="h-8"
                />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">Cancel.</label>
                <Input
                  type="number"
                  min={0}
                  value={metaCancel}
                  onChange={(e) => setMetaCancel(e.target.value)}
                  placeholder="—"
                  className="h-8"
                />
              </div>
            </div>
            <Button
              type="button"
              size="sm"
              className="w-full"
              disabled={saveMetas.isPending}
              onClick={() => saveMetas.mutate()}
            >
              Salvar metas
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/80">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Wallet className="h-4 w-4 text-muted-foreground" />
            Projeção financeira
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Ticket médio das locações do período (soma dos títulos ÷ locação) × cenários de volume até{' '}
            {projecaoCtx?.projecao?.horizonteFimMes ?? 'fim do mês'}
          </p>
        </CardHeader>
        <CardContent className="pt-0">
          {hasFinanceiro && fin ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
              <FinKpiCard
                label="Ticket médio"
                title="Ticket médio — SQL"
                sql={sqlTicket}
                value={formatCurrency(fin.ticketMedio)}
                hint={`${fin.locacoesComTitulo.toLocaleString('pt-BR')} loc. com título`}
              />
              <FinKpiCard
                label="Ticket médio semanal"
                title="Ticket médio semanal — SQL"
                sql={sqlTicketSemanal}
                value={formatCurrency(fin.ticketMedioSemanal)}
                hint={`${fin.semanasPeriodo.toLocaleString('pt-BR', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 1,
                })} sem. no período`}
              />
              <FinKpiCard
                label="Estim. período"
                title="Estimativa do período — SQL"
                sql={sqlEstimPeriodo || null}
                value={formatCurrency(fin.receitaPeriodoEstimada)}
                hint={`${criadas.toLocaleString('pt-BR')} × ticket`}
              />
              <FinKpiCard
                label="Proj. conservador"
                title="Projeção conservadora — SQL"
                sql={sqlProjBase || null}
                value={formatCurrency(fin.projetadoConservador)}
              />
              <FinKpiCard
                label="Proj. base"
                title="Projeção base — SQL"
                sql={sqlProjBase || null}
                highlight
                value={formatCurrency(fin.projetadoBase)}
                hint={`${projecaoCtx?.projecao?.projetadoBase?.toLocaleString('pt-BR') ?? '—'} locações`}
              />
              <FinKpiCard
                label="Proj. agressivo"
                title="Projeção agressiva — SQL"
                sql={sqlProjBase || null}
                value={formatCurrency(fin.projetadoAgressivo)}
              />
              <FinKpiCard
                label="Meta financeira"
                title="Meta financeira — SQL"
                sql={sqlMetaFin}
                value={fin.metaFinanceira != null ? formatCurrency(fin.metaFinanceira) : '—'}
                hint={
                  fin.gapVsMetaFinanceiro != null ? (
                    <span
                      className={`tabular-nums ${
                        fin.gapVsMetaFinanceiro >= 0 ? 'text-emerald-700' : 'text-red-700'
                      }`}
                    >
                      Gap {fin.gapVsMetaFinanceiro >= 0 ? '+' : ''}
                      {formatCurrency(fin.gapVsMetaFinanceiro)}
                    </span>
                  ) : (
                    'Defina meta de locações'
                  )
                }
              />
            </div>
          ) : (
            <p className="py-4 text-sm text-muted-foreground">
              Sem ticket médio no período — locações sem títulos vinculados (ou ainda carregando).
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="border-border/80 bg-muted/10">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-primary" />
            Projetar com IA
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            A Rique usa o histórico de locações, tipos de contrato e metas desta tela. Use o
            microfone no chat se preferir ditar.
          </p>
          <p className="mt-1 text-xs font-medium text-foreground/80">{contextoResumo}</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-full max-w-[9.5rem]">
              <label className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Meta alvo
              </label>
              <Input
                type="number"
                min={0}
                value={metaAlvo}
                onChange={(e) => setMetaAlvo(e.target.value)}
                placeholder="Ex.: 120"
                className="h-9"
                disabled={openingIa}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAnalisarIa();
                }}
              />
            </div>
            <p className="pb-2 text-xs text-muted-foreground">
              Opcional — “quero chegar em N” entra no pedido da Rique
              {metaAlvoNum != null && metaAlvoNum > criadas
                ? ` · faltam ${(metaAlvoNum - criadas).toLocaleString('pt-BR')}`
                : ''}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {chips.map((chip) => (
              <button
                key={chip.id}
                type="button"
                disabled={openingIa}
                onClick={() => handleChipClick(chip)}
                className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-60 ${
                  chip.kind === 'focus-metas'
                    ? 'border-primary/40 bg-primary/5 text-foreground hover:bg-primary/10'
                    : 'border-border bg-background text-foreground hover:border-primary/40 hover:bg-primary/5'
                }`}
              >
                {chip.label}
              </button>
            ))}
          </div>
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-stretch">
            <Input
              value={promptIa}
              onChange={(e) => setPromptIa(e.target.value)}
              placeholder="Ex.: Quero projetar 120 locações no próximo mês e saber o que falta para bater a meta…"
              className="w-full min-w-0 flex-1"
              disabled={openingIa}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAnalisarIa();
              }}
            />
            <Button
              type="button"
              className="w-full shrink-0 sm:w-auto"
              disabled={openingIa}
              onClick={() => handleAnalisarIa()}
            >
              {openingIa ? 'Abrindo…' : 'Analisar com IA'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <DiretoriaLocacoesDetalheModal
        open={Boolean(detalheKind)}
        onClose={() => setDetalheKind(null)}
        kind={detalheKind}
        companyId={companyId || undefined}
        periodFrom={periodFrom}
        periodTo={periodTo}
        subtitle={
          detalheKind === 'canceladas_mes_ref'
            ? `Numerador do turnover · ${(locacoesAtivas ?? 0).toLocaleString('pt-BR')} ativas no snapshot`
            : `Período ${periodLabel}`
        }
      />

      <DiretoriaSimularModal
        open={simularOpen}
        onClose={() => setSimularOpen(false)}
        companyId={companyId}
        periodFrom={periodFrom}
        periodTo={periodTo}
        periodLabel={periodLabel}
        ano={anoMes.ano}
        mes={anoMes.mes}
        criadas={criadas}
        canceladas={canceladas}
        mensal={mensal}
        tiposLocacao={tiposLocacao}
      />
    </section>
  );
};
